import { Link } from "react-router-dom";
import { Dog, Cat, Snowflake, Home, Car, AlertTriangle, Stethoscope, Ban, Sparkles } from "lucide-react";
import type { Article } from "./types";
import { articlesMeta } from "./_meta";
import { SectionHeading } from "@/components/article/SectionHeading";
import { Callout } from "@/components/article/Callout";

const META = articlesMeta.find((m) => m.slug === "pies-i-kot-w-upal-jak-schlodzic")!;

const Body = () => (
  <>
    <p>
      Upał może być dla psa lub kota niebezpieczny. Zwierzę może się odwodnić, przegrzać albo poparzyć łapy o
      gorący asfalt. Najbardziej narażone są zwierzęta <strong>starsze, chore, otyłe, bardzo młode, długowłose</strong>{" "}
      i psy z krótkim pyszczkiem.
    </p>
    <p>Poniżej znajdziesz konkretną listę kontrolną, co zrobić w gorące dni.</p>

    <SectionHeading id="pies" icon={Dog}>
      Pies podczas upału - najważniejsze zasady
    </SectionHeading>
    <p>
      W największy upał nie planuj długich spacerów. Najlepiej wychodzić <strong>wcześnie rano i wieczorem</strong>.
      W środku dnia zrób tylko krótkie wyjście na potrzeby.
    </p>
    <p>Unikaj:</p>
    <ul>
      <li>spacerów po rozgrzanym asfalcie,</li>
      <li>biegania z psem,</li>
      <li>aportowania w pełnym słońcu,</li>
      <li>jazdy rowerem z psem,</li>
      <li>długiego stania pod sklepem,</li>
      <li>kagańców, które utrudniają ziajanie.</li>
    </ul>

    <Callout variant="tip" title="Test dłoni na chodniku">
      <p>
        Przed spacerem dotknij chodnika dłonią. Jeśli jest za gorący dla Ciebie, to <strong>może poparzyć psie łapy</strong>.
        Trzymaj dłoń przez 5 sekund - jeśli nie wytrzymujesz, wróć po zmroku.
      </p>
    </Callout>

    <p>
      Na dłuższe wyjście zabierz wodę. Jeśli pies mocno zieje, zwalnia, szuka cienia albo nie chce iść dalej,
      wróć do domu.
    </p>

    <SectionHeading id="kot" icon={Cat}>
      Kot podczas upału - na co uważać?
    </SectionHeading>
    <p>
      Kot też może się przegrzać. Cień i chłodna podłoga to za mało, gdy w mieszkaniu jest ponad 30 stopni.
    </p>
    <p>W domu warto:</p>
    <ul>
      <li>postawić kilka misek z wodą,</li>
      <li>częściej wymieniać wodę,</li>
      <li>dodać trochę wody do mokrej karmy,</li>
      <li>zostawić dostęp do łazienki, płytek lub innego chłodniejszego miejsca,</li>
      <li>zasłonić okna od strony słońca,</li>
      <li>wietrzyć rano i wieczorem,</li>
      <li>regularnie wyczesywać sierść.</li>
    </ul>
    <Callout variant="warning">
      <p>
        Nie zamykaj kota w nagrzanym pokoju ani na balkonie bez cienia i wody.
      </p>
    </Callout>

    <SectionHeading id="schlodzic-psa" icon={Snowflake}>
      Jak schłodzić psa w domu?
    </SectionHeading>
    <p>Nie każdy pies lubi wodę, ale warto dać mu kilka bezpiecznych opcji do wyboru.</p>
    <p>Możesz spróbować:</p>
    <ul>
      <li>położyć matę chłodzącą (poduszkę żelową lub samochodową, która absorbuje ciepło) w miejscu, gdzie pies lubi odpoczywać,</li>
      <li>rozstawić płytki basenik z wodą w cieniu,</li>
      <li>zwilżyć psu łapy, brzuch i klatkę piersiową chłodną wodą,</li>
      <li>przygotować mrożonego Konga (gumowa zabawka do wypełniania smakołykami) albo matę do lizania,</li>
      <li>wymieszać mokrą karmę z odrobiną wody, wlać do zabawki lub miseczki i zamrozić,</li>
      <li>położyć przy legowisku zamrożoną butelkę owiniętą ręcznikiem,</li>
      <li>ustawić wentylator tak, żeby pies mógł odejść od nawiewu.</li>
    </ul>
    <Callout variant="warning">
      <p>
      Uważaj z matami chłodzącymi - niektóre mają trujące wnętrze (żel lub substancje chemiczne). Jeśli pies
        gryzie zabawki i inne przedmioty, obserwuj, czy nie próbuje jej rozdrapać ani połknąć.
      </p>
    </Callout>
    <Callout variant="tip" title="Mata do lizania - spokojne zajęcie w upał">
      <p>
        Dobry pomysł na spokojne zajęcie psa to mata do lizania z mokrą karmą, jogurtem naturalnym bez cukru
        albo karmą rozmieszaną z wodą. Można ją lekko zamrozić. Pies się chłodzi, zajmuje głowę i nie nakręca
        się bieganiem.
      </p>
    </Callout>
    <p>
      Jeśli używasz lodu, rób to rozsądnie. Nie dawaj dużych kostek małemu psu, jeśli mógłby się nimi zadławić.
      Przy podejrzeniu udaru cieplnego lód i przekąski nie wystarczą - wtedy trzeba chłodzić ciało i skontaktować
      się z weterynarzem.
    </p>

    <SectionHeading id="schlodzic-kota" icon={Snowflake}>
      Jak schłodzić kota w domu?
    </SectionHeading>
    <p>Kota nie trzeba wkładać do wody ani zmuszać do chłodzenia. Lepiej dać mu opcje, z których sam skorzysta.</p>
    <p>Możesz spróbować:</p>
    <ul>
      <li>postawić płytką miskę lub tackę z wodą i wrzucić do niej piłeczki pingpongowe albo większe kostki lodu,</li>
      <li>położyć kostkę lodu na płytkach, żeby kot mógł ją przesuwać łapką,</li>
      <li>zamrozić wodę z tuńczyka w sosie własnym i podać jako małą, okazjonalną przekąskę,</li>
      <li>zwilżyć dłonie chłodną wodą i delikatnie pogłaskać kota,</li>
      <li>postawić fontannę (poidełko z płynącą wodą, które zachęca kota do picia) albo kilka misek z wodą w różnych miejscach,</li>
      <li>położyć przy legowisku zamrożoną butelkę owiniętą ręcznikiem.</li>
    </ul>
    <Callout variant="tip" title="Trik: tacka z pływającymi zabawkami">
      <p>
        Fajny trik dla kota to płytka tacka z wodą i pływającymi zabawkami (np. piłeczki pingpongowe). Kot może
        łowić je łapką, bawić się i przy okazji chłodzić opuszki.
      </p>
    </Callout>
    <Callout variant="tip" title="Kostka lodu na płytkach - prosta zabawa">
      <p>
        Możesz położyć kostkę lodu na płytkach, żeby kot mógł ją przesuwać łapką. To prosta zabawa, która przy
        okazji lekko chłodzi opuszki. Rób to pod nadzorem i nie zmuszaj kota, jeśli nie jest zainteresowany.
      </p>
    </Callout>
    <p>
      Jeśli chcesz zrobić kocią lodową przekąskę, używaj tylko bezpiecznych składników. Może to być np. woda z
      tuńczyka w sosie własnym albo bardzo rozcieńczony bulion <strong>bez soli, cebuli, czosnku i przypraw</strong>.
      Podawaj małą ilość i tylko okazjonalnie - bardziej jako dodatek niż posiłek.
    </p>

    <SectionHeading id="mieszkanie" icon={Home}>
      Jak schłodzić mieszkanie dla zwierzęcia?
    </SectionHeading>
    <p>Najprościej:</p>
    <ul>
      <li>zasłoń okna w najgorętszej części dnia,</li>
      <li>zostaw dostęp do chłodnego pomieszczenia,</li>
      <li>wietrz rano i wieczorem,</li>
      <li>użyj wentylatora, ale tak, żeby zwierzę mogło odejść od nawiewu,</li>
      <li>połóż matę chłodzącą, jeśli zwierzę ją lubi,</li>
      <li>połóż wilgotny ręcznik na podłodze,</li>
      <li>podawaj mokrą karmę z dodatkiem wody.</li>
    </ul>
    <Callout variant="warning" title="Nie przykrywaj przegrzanego zwierzęcia mokrym ręcznikiem">
      <p>
        Taki ręcznik może <strong>zatrzymać ciepło</strong>. Lepiej położyć go pod zwierzęciem albo delikatnie
        chłodzić ciało wodą.
      </p>
    </Callout>

    <SectionHeading id="samochod" icon={Car}>
      Zwierzę w samochodzie - tego nie rób nigdy
    </SectionHeading>
    <Callout variant="warning" title="Nigdy nie zostawiaj psa ani kota w samochodzie">
      <p>
        Nawet na chwilę. Nawet przy uchylonej szybie. Nawet jeśli auto stoi w cieniu. Samochód bardzo szybko się
        nagrzewa i może stać się dla zwierzęcia <strong>śmiertelną pułapką</strong>.
      </p>
    </Callout>
    <p>Jeśli nie możesz zabrać zwierzęcia ze sobą, zostaw je w domu.</p>

    <SectionHeading id="objawy" icon={AlertTriangle}>
      Objawy przegrzania u psa lub kota
    </SectionHeading>
    <p>Zareaguj szybko, jeśli widzisz:</p>
    <ul>
      <li>bardzo mocne ziajanie,</li>
      <li>szybki lub ciężki oddech,</li>
      <li>ślinienie się,</li>
      <li>osłabienie,</li>
      <li>chwiejny chód,</li>
      <li>wymioty,</li>
      <li>biegunkę,</li>
      <li>brak reakcji,</li>
      <li>drgawki,</li>
      <li>utratę przytomności.</li>
    </ul>
    <Callout variant="warning" title="U kota wczesne objawy są łatwe do przeoczenia">
      <p>
        Kot nie pokaże od razu, że jest za gorąco. Zamiast dyszeć może być apatyczny, wycofany albo leżeć
        bez sił w najchłodniejszym miejscu. Dopiero gdy zacznie dyszeć, sytuacja jest poważna - <strong>koty
        rzadko zieją, więc każde dyszenie to sygnał alarmowy</strong>.
      </p>
    </Callout>

    <SectionHeading id="pierwsza-pomoc" icon={Stethoscope}>
      Co zrobić przy podejrzeniu przegrzania?
    </SectionHeading>
    <ol>
      <li>Przenieś zwierzę w cień lub chłodne miejsce.</li>
      <li>Przerwij spacer, zabawę albo wysiłek.</li>
      <li>Podaj wodę, jeśli zwierzę jest w stanie pić.</li>
      <li>Chłodź ciało wodą chłodniejszą niż temperatura zwierzęcia (nielodowatą).</li>
      <li>Nie polewaj na siłę głowy, jeśli zwierzę ma problem z oddychaniem.</li>
      <li>Skontaktuj się z weterynarzem.</li>
    </ol>
    <p>
      Nawet jeśli pies lub kot po chwili wygląda lepiej, warto skonsultować się z lekarzem weterynarii.
      Przegrzanie może być bardzo niebezpieczne.
    </p>

    <SectionHeading id="czego-nie-robic" icon={Ban}>
      Czego nie robić w upał?
    </SectionHeading>
    <ul>
      <li>nie zmuszaj psa ani kota do wody,</li>
      <li>nie rób lodowatej kąpieli,</li>
      <li>nie zostawiaj zwierzęcia samego z zabawką, którą może pogryźć lub połknąć,</li>
      <li>nie podawaj bulionu z solą, cebulą, czosnkiem albo przyprawami,</li>
      <li>nie zostawiaj psa ani kota w samochodzie,</li>
      <li>nie planuj intensywnej zabawy w pełnym słońcu,</li>
      <li>nie ignoruj dyszenia u kota.</li>
    </ul>

    <SectionHeading id="najwazniejsze" icon={Sparkles}>
      Najważniejsze
    </SectionHeading>
    <Callout variant="check" title="Prosto i konkretnie">
      <p>
        W upał najważniejsze są proste rzeczy: <strong>woda, cień, krótsze spacery</strong> i szybka reakcja,
        jeśli zwierzę wygląda źle.
      </p>
    </Callout>
    <p>
      Możesz też ułatwić zwierzęciu chłodzenie w domu: dać psu mrożoną matę do lizania, kotu tackę z wodą i
      zabawkami albo po prostu zostawić dostęp do chłodnych płytek.
    </p>
    <p>
      Nie musisz robić nic skomplikowanego. Lepiej odpuścić spacer w południe niż ryzykować przegrzanie psa
      albo poparzenie łap.
    </p>

    <div className="not-prose my-8 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 p-5 sm:p-6 text-center">
      <p className="text-base sm:text-lg font-semibold text-foreground mb-3">
        Szukasz schroniska albo fundacji w swojej okolicy?
      </p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-bold px-5 py-2.5 rounded-full text-sm hover:bg-primary/90 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5"
      >
        Otwórz mapę schronisk
      </Link>
    </div>
  </>
);

const article: Article = {
  slug: META.slug,
  title: META.title,
  description: META.description,
  publishedAt: META.publishedAt,
  readingMinutes: 7,
  category: META.category,
  excerpt: META.excerpt,
  Body,
};

export default article;
